import { useDisclosure } from "@heroui/react";
import { InputField, ModalBuilder, ModalError } from "../modalBuilder";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { api } from "@/api";
import { toast } from "react-toastify";
import { Requisite } from "@/api/requisites/requisites.ts";

interface IEditRequisiteForm {
  display_name: string;
  inn: number;
  kpp: number;
  ogrn: number;
  bank_account: string;
  bank_name: string;
  bik: string;
  correspondent_account: string;
  address: string;
  phone: string;
  email: string;
  currency: number;
}

const editRequisiteFields: InputField[] = [
  {
    label: "Название",
    name: "display_name",
    placeholder: "Название счёта",
    type: "text",
    isRequired: true,
  },
  {
    label: "ИНН",
    name: "inn",
    placeholder: "1234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "КПП",
    name: "kpp",
    placeholder: "123456789",
    type: "text",
    isRequired: false,
  },
  {
    label: "ОГРН",
    name: "ogrn",
    placeholder: "1234567890123",
    type: "text",
    isRequired: false,
  },
  {
    label: "Расчетный счет",
    name: "bank_account",
    placeholder: "12345678901234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "Название банка",
    name: "bank_name",
    placeholder: "Название банка",
    type: "text",
    isRequired: true,
  },
  {
    label: "Валюта",
    name: "currency",
    placeholder: "Валюта",
    type: "currency",
    isRequired: true,
  },
  {
    label: "БИК",
    name: "bik",
    placeholder: "123456789",
    type: "text",
    isRequired: false,
  },
  {
    label: "Корреспондентский счет",
    name: "correspondent_account",
    placeholder: "12345678901234567890",
    type: "text",
    isRequired: true,
  },
  {
    label: "Адрес",
    name: "address",
    placeholder: "Адрес компании",
    type: "text",
    isRequired: true,
  },
  {
    label: "Телефон",
    name: "phone",
    placeholder: "+71234567890",
    pattern: /^\+7\d{10}$/,
    type: "text",
    isRequired: true,
  },
  {
    label: "Email",
    name: "email",
    placeholder: "email@domain.com",
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    type: "text",
    isRequired: true,
  },
];

export function EditRequisiteModal({
  disclosure,
  requisite,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  requisite: Requisite | null;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);
  const renterId = useAppSelector((state) => state.location.location.id);

  const handleError = useErrorHandling();

  const onSubmit = (data: IEditRequisiteForm) => {
    return new Promise<Boolean>(async (resolve, _reject) => {
      api.requisites
        .update(requisite!.id, Object.assign({ renter_id: renterId }, data))
        .then((_data) => {
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<Boolean>(async (resolve, _reject) => {
      api.requisites
        .delete(requisite!.id)
        .then((_data) => {
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <ModalBuilder
      title="Редактирование счёта"
      modalErrors={modalErrors}
      fields={editRequisiteFields}
      isOpen={isOpen}
      isSensitiveData={true}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      onDelete={onDelete}
      submitButtonText="Сохранить"
      defaultValues={{
        display_name: requisite?.display_name,
        inn: requisite?.inn,
        kpp: requisite?.kpp,
        ogrn: requisite?.ogrn,
        bank_account: requisite?.bank_account,
        bank_name: requisite?.bank_name,
        bik: requisite?.bik,
        correspondent_account: requisite?.correspondent_account,
        address: requisite?.address,
        phone: requisite?.phone,
        email: requisite?.email,
        currency: requisite?.currency,
      }}
    />
  );
}
